import { FIELD_CONFIG, CAMPAIGN_USERS_FIELD_CONFIG } from "./constants";

/**
 * Get available operators for a specific field type
 * @param {string} fieldType - The field type (e.g., "First Name", "Total Delivered")
 * @param {boolean} campaignUsers - Whether this is for campaign users context
 * @returns {Array} Array of available operators for the field
 */
export const getOperatorsForField = (fieldType, campaignUsers = false) => {
  if (!fieldType)
    return ["Is", "Is Not", "Like", "Not Like", "Is Empty", "Is Not Empty"];

  const config = campaignUsers ? CAMPAIGN_USERS_FIELD_CONFIG : FIELD_CONFIG;
  return (
    config[fieldType]?.operators || [
      "Is",
      "Is Not",
      "Like",
      "Not Like",
      "Is Empty",
      "Is Not Empty",
    ]
  );
};

/**
 * Check if value input should be shown based on operator
 * @param {string} operator - The selected operator
 * @returns {boolean} Whether to show value input
 */
export const shouldShowValueInput = (operator) => {
  if (!operator || typeof operator !== "string") return true;
  return !["Is Empty", "Is Not Empty"].includes(operator);
};

/**
 * Get the input type for value based on field type and operator
 * @param {string} fieldType - The field type
 * @param {string} operator - The selected operator
 * @returns {string} The input type ('disabled', 'select', 'date', 'autocomplete', 'text')
 */
export const getValueInputType = (fieldType, operator) => {
  if (!fieldType || !operator) return "text";
  if (!shouldShowValueInput(operator)) return "disabled";

  // Status fields
  if (fieldType === "emailStatus") return "select";
  if (fieldType === "User Status") return "select";
  if (fieldType === "sentFromEmail") return "select";

  // Date fields
  if (
    typeof fieldType === "string" &&
    (fieldType.includes("Date") ||
      fieldType === "Created At" ||
      fieldType === "Activity Date" ||
      fieldType === "activityDate")
  )
    return "date";

  // Event fields
  if (fieldType === "Campaign Event") return "autocomplete";

  // Boolean fields (for campaign users)
  if (
    typeof fieldType === "string" &&
    (["blacklisted", "red flagged", "verified"].includes(
      fieldType.toLowerCase(),
    ) ||
      ["Blacklisted", "Red Flagged", "Verified"].includes(fieldType))
  )
    return "autocomplete";

  // Numeric fields - check if field is numeric type in either config
  if (
    FIELD_CONFIG[fieldType]?.type === "numeric" ||
    CAMPAIGN_USERS_FIELD_CONFIG[fieldType]?.type === "numeric"
  ) {
    return "number";
  }

  // Default to text input
  return "text";
};

/**
 * Check if operator is valid for field type
 * @param {string} fieldType - The field type
 * @param {string} operator - The operator to check
 * @param {boolean} campaignUsers - Whether this is for campaign users context
 * @returns {boolean} Whether the operator is valid for the field
 */
export const isValidOperatorForField = (
  fieldType,
  operator,
  campaignUsers = false,
) => {
  if (!fieldType || !operator) return false;

  const availableOperators = getOperatorsForField(fieldType, campaignUsers);
  return (
    Array.isArray(availableOperators) && availableOperators.includes(operator)
  );
};

/**
 * Reset operator if it's not valid for the new field type
 * @param {string} currentOperator - Current operator
 * @param {string} newFieldType - New field type
 * @param {boolean} campaignUsers - Whether this is for campaign users context
 * @returns {string} Valid operator for the field type
 */
export const getValidOperatorForField = (
  currentOperator,
  newFieldType,
  campaignUsers = false,
) => {
  if (!newFieldType || !currentOperator) return "Is";

  if (isValidOperatorForField(newFieldType, currentOperator, campaignUsers)) {
    return currentOperator;
  }

  // Return first available operator as default
  const availableOperators = getOperatorsForField(newFieldType, campaignUsers);
  return Array.isArray(availableOperators) && availableOperators.length > 0
    ? availableOperators[0]
    : "Is";
};

// Domain must be label(s) + dot + TLD — do not put "." inside the label class
// or "user@adrich.io" fails (greedy [^.]+ would eat "adrich.io" and leave nothing for \.tld).
export const EMAIL_REGEX = /^[A-Z0-9._%+-]+@(?:[A-Z0-9-]+\.)+[A-Z]{2,}$/i;

export const getNormalizedEmailValues = (value) => {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === "string" && item.trim());
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
};

export const extractValidEmails = (rawValue) => {
  return (rawValue || "")
    .split(/[\n,\s;]+/)
    .map((item) => item.trim())
    .filter((item) => item && EMAIL_REGEX.test(item));
};

export const mergeUniqueEmails = (baseEmails, incomingEmails) => {
  const merged = [...baseEmails, ...incomingEmails];
  return merged.filter(
    (email, index) =>
      merged.findIndex(
        (candidate) => candidate.toLowerCase() === email.toLowerCase(),
      ) === index,
  );
};

export const parseEmailsFromCsvContent = (content) => {
  return content.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
};
